import { useEffect, useMemo, useState } from "react";

const PROGRESS_INTERVAL_IN_MS = 50;

/**
 * Generic hook to reveal a possibly large array in successive chunks during
 * browser idle periods.  This avoids long blocking tasks while keeping UI
 * responsive.
 *
 * @param items       Full array of items that should eventually be visible.
 * @param  chunkSize   Number of items to add on each idle callback
 * 
 */

export const useProgressiveRender = <T>(items: T[], chunkSize = 200) => {
  const [visibleItems, setVisibleItems] = useState<T[]>([]);

  useEffect(() => {
    let cancelled = false;

    const scheduleIdle = (callback: () => void) => {
      if (typeof window.requestIdleCallback === "function")
        return window.requestIdleCallback(callback);

      return window.setTimeout(callback, PROGRESS_INTERVAL_IN_MS);
    };

    setVisibleItems(items.slice(0, chunkSize));

    let index = chunkSize;

    const appendChunk = () => {
      if (cancelled) return;

      index = Math.min(index + chunkSize, items.length);
      setVisibleItems(items.slice(0, index));

      if (index < items.length) scheduleIdle(appendChunk);
    };

    if (index < items.length) {
      scheduleIdle(appendChunk);
    }

    return () => {
      cancelled = true;
    };
  }, [items, chunkSize]);

  const isRendering = useMemo(
    () => visibleItems.length < items.length,
    [visibleItems, items]
  );

  const renderProgress = useMemo(
    () => Math.round((visibleItems.length / items.length) * 100),
    [visibleItems, items]
  );

  return { visibleItems, isRendering, renderProgress };
};
